import api from '../lib/api';
import { formatDistrict } from './districtDisplay';

/**
 * Utility functions to get display names for entities by ID
 */

export interface EntityDisplayInfo {
  id: string;
  display_name: string;
  entity_type: string;
}

/**
 * Get display name for a legislator by ID
 */
export const getLegislatorDisplayName = async (legislatorId: string): Promise<string> => {
  try {
    const response = await api.get(`/legislators/${legislatorId}`);
    if (response.data.success && response.data.data) {
      const legislator = response.data.data;
      return `${legislator.full_name} (${formatDistrict(null, String(legislator.district ?? ''), legislator.chamber)})`;
    }
  } catch (error) {
    console.error('Error fetching legislator display name:', error);
  }
  return `Legislator ${legislatorId}`;
};

/**
 * Get display name for a bill by ID
 */
export const getBillDisplayName = async (billId: string): Promise<string> => {
  try {
    const response = await api.get(`/bills/${billId}`);
    if (response.data.success && response.data.data) {
      const bill = response.data.data.bill; // Bill data is nested under 'bill' property
      if (bill && bill.number && bill.title) {
        return `${bill.number} - ${bill.title}`;
      }
    }
  } catch (error) {
    console.error('Error fetching bill display name:', error);
  }
  return `Bill ${billId}`;
};

/**
 * Get display name for a calendar event by ID
 */
export const getCalendarEventDisplayName = async (eventId: string, workspaceId?: string): Promise<string> => {
  try {
    const params = workspaceId ? { workspace_id: workspaceId } : {};
    const response = await api.get(`/calendar/events/${eventId}`, { params });
    if (response.data.success && response.data.data) {
      const event = response.data.data;
      const date = event.reading_date ? new Date(event.reading_date).toLocaleDateString() : 'No Date';
      return `${event.reading_title || 'Untitled Event'} (${date})`;
    }
  } catch (error) {
    console.error('Error fetching calendar event display name:', error);
  }
  return `Event ${eventId}`;
};

/**
 * Get display name for any entity by type and ID
 */
export const getEntityDisplayName = async (entityType: string, entityId: string, workspaceId?: string): Promise<string> => {
  switch (entityType) {
    case 'legislator':
      return getLegislatorDisplayName(entityId);
    case 'bill':
      return getBillDisplayName(entityId);
    case 'calendar_event':
      return getCalendarEventDisplayName(entityId, workspaceId);
    default:
      return `${entityType} ${entityId}`;
  }
};

/**
 * Get display info for multiple entities
 */
export const getEntityDisplayInfos = async (entities: { entity_type: string; entity_id: string }[]): Promise<EntityDisplayInfo[]> => {
  const promises = entities.map(async (entity) => {
    const display_name = await getEntityDisplayName(entity.entity_type, entity.entity_id);
    return {
      id: entity.entity_id,
      display_name,
      entity_type: entity.entity_type
    };
  });
  
  return Promise.all(promises);
};
